import Konva from 'konva';
import { createContext, useContext, ReactNode, useReducer, MutableRefObject, } from 'react';

type RefsState = {
	sliderRef: MutableRefObject<HTMLInputElement | null> | null;
	konvaRef: MutableRefObject<Konva.Stage | null> | null
	movablePanelRef: MutableRefObject<Konva.Line | null> | null;
	desktopViewWrapRef: MutableRefObject<HTMLDivElement | null> | null;
	konvaBottomDivRef: MutableRefObject<HTMLDivElement | null> | null;
	sidePanelDivRef: MutableRefObject<HTMLDivElement | null> | null;
	isDrawingRef: MutableRefObject<boolean>;
	startPointRef: MutableRefObject<{ x: number; y: number } | null>;
	userDrawnObsRef: MutableRefObject<Konva.Group | null>;
	selectFacetRef: MutableRefObject<((facetId: string) => void) | null>;
};

type RefPayload = {
	ref: RefsState['sliderRef']
	| RefsState['konvaRef']
	| RefsState['movablePanelRef']
	| RefsState['desktopViewWrapRef']
	| RefsState['konvaBottomDivRef']
	| RefsState['sidePanelDivRef'];
}

type ActionType = 'ADD_SLIDER_REF' | 'ADD_KONVA_REF' | 'ADD_LAST_POLYGON_REF' | 'ADD_PANEL_REF' 
| 'ADD_DESKTOP_VIEW_WRAP_REF' | 'ADD_SIDE_PANEL_DIV_REF' | 'KONVA_BOTTOM_DIV_REF' | 'RESET_DRAWN_OBS_REF';

type Action = { type: ActionType, payload: RefPayload };

function RefsReducer(refsState: RefsState, action: Action): RefsState {
	const { ref } = action.payload;
	switch (action.type) {
	case 'ADD_SLIDER_REF': {
		return {
			...refsState,
			sliderRef: ref as unknown as RefsState['sliderRef']
		};
	}
	case 'ADD_KONVA_REF': {
		if (!ref) return refsState;
		return {
			...refsState,
			konvaRef: ref as unknown as RefsState['konvaRef']
		};
	}
	case 'ADD_PANEL_REF': {
		return {
			...refsState,
			movablePanelRef: ref as unknown as RefsState['movablePanelRef']
		};
	}
	case 'ADD_DESKTOP_VIEW_WRAP_REF': {
		return {
			...refsState,
			desktopViewWrapRef: ref as unknown as RefsState['desktopViewWrapRef']
		};
	}
	case 'ADD_SIDE_PANEL_DIV_REF': {
		return {
			...refsState,
			sidePanelDivRef: ref as unknown as RefsState['sidePanelDivRef']
		};
	}
	case 'KONVA_BOTTOM_DIV_REF': {
		return {
			...refsState,
			konvaBottomDivRef: ref as unknown as RefsState['konvaBottomDivRef']
		};
	}
	case 'RESET_DRAWN_OBS_REF': {
		if (refsState.isDrawingRef) refsState.isDrawingRef.current = false;
		if (refsState.startPointRef) refsState.startPointRef.current = null;
		if (refsState.userDrawnObsRef) refsState.userDrawnObsRef.current = null;	
		return {
			...refsState,
		};
	}

	default: {
		throw Error('Unknown action: ' + action.type);
	}
	}
}

const initialState: RefsState = {
	sliderRef: null,
	konvaRef: null,
	movablePanelRef: null,
	desktopViewWrapRef: null,
	konvaBottomDivRef: null,
	sidePanelDivRef: null,
	isDrawingRef: { current: false },
	userDrawnObsRef: { current: null },
	startPointRef: { current: null },
	selectFacetRef: { current: null },
};

const RefContext = createContext<RefsState | undefined>(undefined);
const RefDispatchContext = createContext<React.Dispatch<Action> | undefined>(undefined);

export function RefProvider(props: { children: ReactNode }) {
	const [state, dispatch] = useReducer(RefsReducer, initialState);

	return (
		<RefContext.Provider value={state}>
			<RefDispatchContext.Provider value={dispatch}>
				{props.children}
			</RefDispatchContext.Provider>
		</RefContext.Provider>
	);
}

export function useRefs() {
	const context = useContext(RefContext);
	if (context === undefined) {
		throw new Error('useRefs must be used within a ToolProvider');
	}
	return context;
}

export function useRefDispatch() {
	const context = useContext(RefDispatchContext);
	if (context === undefined) {
		throw new Error('useRefDispatch must be used within a ToolProvider');
	}
	return context;
}

RefContext.displayName = 'RefContext';
